import Link from '@/components/Link/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { useTranslation } from 'react-i18next';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { formatPriceString } from '@/utils/numbers';
import { useConfig } from '@/hooks/useConfig';
import Loading from '@/components/Loading/Loading';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { PaymentIcon } from '../utils';
import { PaymentProvider } from '@/hooks/invoice/types';
import { usePaymentSession } from '@/hooks/payment/usePaymentSession';
import { useInvoice } from '@/hooks/invoice/useInvoice';

const PayConfirmation = () => {
  const navigate = useNavigate();
  const { paymentSessionId: paymentSessionIdFromParams } = useParams();
  const { t } = useTranslation();
  const { urls } = useConfig();
  const { refetchInvoice } = useInvoice();
  const { paymentSession, isLoading, queryError, setPaymentSessionId, resetPaymentSession } = usePaymentSession();

  useEffect(() => {
    if (paymentSessionIdFromParams) {
      setPaymentSessionId(paymentSessionIdFromParams);
    }
  }, [paymentSessionIdFromParams]);

  useEffect(() => {
    if (queryError) {
      navigate('/pay/error', { replace: true });
    }
  }, [queryError]);

  if (isLoading || !paymentSession) {
    return <Loading height='50vh' />;
  }

  const handleContinuePayment = async () => {
    resetPaymentSession();
    await refetchInvoice();
  };

  return (
    <Stack justifyContent='center' alignItems='center' gap={4}>
      <Typography component='h1' variant='h2' fontWeight='bold'>
        {t('page.confirmation.thank-you')}
      </Typography>

      <CheckCircle color='success' fontSize='large' />

      <Card>
        <CardHeader
          title={t(
            paymentSession.invoiceStatus?.toLowerCase() === 'partiallypaid'
              ? 'common-phrases.partial-payment'
              : 'components.payment-option-selector.total-amount'
          )}
          subheader={formatPriceString(paymentSession?.payment.amount)}
        />

        <CardContent>
          <Stack gap={1}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='body2' fontWeight='bold'>
                {t('common-phrases.reference-number')}
              </Typography>
              <Typography variant='body2'>{paymentSession?.referenceCode}</Typography>
            </Stack>

            <Stack direction='row' alignItems='center' justifyContent='space-between'>
              <Typography variant='body2' fontWeight='bold'>
                {t('page.confirmation.payment-method')}
              </Typography>
              <Stack gap={0.5} direction='row' alignItems='center'>
                <PaymentIcon
                  size='small'
                  provider={paymentSession.payment.methodCode as PaymentProvider}
                  alt={paymentSession.payment.methodText}
                />
                <Typography variant='body2'>{paymentSession.payment.methodText}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {paymentSession.invoiceStatus?.toLowerCase() === 'partiallypaid' && (
        <>
          <Typography variant='body2'>
            {t('page.confirmation.remaining-amount-to-pay', {
              amount: formatPriceString(paymentSession.invoiceRemainingAmount)
            })}
          </Typography>

          <Box>
            <Typography variant='body2'>
              {t('page.confirmation.to-continue-partially-paid', { reference: paymentSession.referenceCode })}
            </Typography>
            <Stack>
              <Link
                align='center'
                variant='body2'
                onClick={handleContinuePayment}
                href={`/pay/${paymentSession.referenceCode}`}
                color='inherit'
              >
                {t('common-phrases.click-here')}
              </Link>
            </Stack>
          </Box>
        </>
      )}

      <Typography variant='body2'>
        {t('common-phrases.go-to')}{' '}
        <Link href={urls.myPages} target='_blank'>
          {t('common-phrases.my-pages')}
        </Link>{' '}
        {t('page.confirmation.to-download-invoice')}
      </Typography>
    </Stack>
  );
};

export default PayConfirmation;
